import React, { useEffect, useState } from "react";
import {
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Snackbar,
} from "@mui/material";
import ListPagination from "./ListPagination";
import ListHeader from "./ListHeader";
import { IInternalClient } from "../../pages/integrations/shared/services/internalClientsService";
import { IPaginationParams } from "../../interfaces/table";
import "../../styles/tables.css";

interface IDisableActionProps {
  disabled?: boolean;
  id: number;
  name: string;
  handleDisableAction: (
    _disableValue: boolean,
    _id: number,
    _name: string,
  ) => void;
  resetValue: boolean;
}

interface IListInternalClientsProps {
  clients: IInternalClient[];
  handlePageChange: (_newPage: number) => void;
  pagination: IPaginationParams;
  showStatusColumn?: boolean;
  handleDisableAction?: (
    _disableValue: boolean,
    _id: number,
    _name: string,
  ) => void;
  handleSelectedAction?: (_option: string) => void;
  setSelectedClient?: (_client: IInternalClient) => void;
  resetValue?: boolean;
}

const DisableAction: React.FC<IDisableActionProps> = ({
  handleDisableAction,
  disabled,
  id,
  name,
  resetValue,
}) => {
  const [disableValue, setDisableValue] = useState(!disabled);

  useEffect(() => {
    setDisableValue(!disabled);
  }, [resetValue, disabled]);

  return (
    <TableCell>
      <Switch
        size="small"
        onClick={() => {
          handleDisableAction(disableValue, id, name);
          setDisableValue(!disableValue);
        }}
        checked={disableValue}
      />
    </TableCell>
  );
};

interface ITableActionsProps {
  client: IInternalClient;
  setSelectedClient: (_client: IInternalClient) => void;
  handleSelectedAction: (_option: string) => void;
}

const TableActions: React.FC<ITableActionsProps> = ({
  client,
  setSelectedClient,
  handleSelectedAction,
}) => (
  <TableCell>
    <Tooltip arrow title="Ver" placement="top">
      <IconButton
        size="small"
        color="primary"
        onClick={() => {
          setSelectedClient(client);
          handleSelectedAction("info");
        }}
      >
        <span className="material-symbols-rounded">visibility</span>
      </IconButton>
    </Tooltip>
    <Tooltip arrow title="Eliminar" placement="top">
      <IconButton
        size="small"
        color="primary"
        onClick={() => {
          setSelectedClient(client);
          handleSelectedAction("delete");
        }}
      >
        <span className="material-symbols-rounded">delete</span>
      </IconButton>
    </Tooltip>
  </TableCell>
);

const ApiKeyCell: React.FC<{ apiKey?: string | null }> = ({ apiKey }) => {
  const [snackOpen, setSnackOpen] = useState(false);

  if (!apiKey) return <TableCell>-</TableCell>;

  const truncated = `${apiKey.slice(0, 8)}…${apiKey.slice(-4)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey).then(() => setSnackOpen(true));
  };

  return (
    <TableCell>
      <Tooltip arrow title="Copiar API Key" placement="top">
        <IconButton size="small" onClick={handleCopy} sx={{ gap: 0.5 }}>
          <span style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
            {truncated}
          </span>
          <span className="material-symbols-rounded" style={{ fontSize: "1rem" }}>
            content_copy
          </span>
        </IconButton>
      </Tooltip>
      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        message="API Key copiada"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </TableCell>
  );
};

/**
 * Table component for displaying internal clients (pending or accepted)
 * Used by InternalInvitations and InternalIntegrations pages
 */
const ListInternalClients: React.FC<IListInternalClientsProps> = ({
  clients,
  handlePageChange,
  pagination: { count, page },
  showStatusColumn = false,
  handleDisableAction,
  handleSelectedAction,
  setSelectedClient,
  resetValue = false,
}) => {
  // Define columns based on whether this is for invitations (with status) or integrations (with actions)
  const tableColumns = showStatusColumn
    ? [
      { label: "Correo electrónico" },
      { label: "Alias" },
      { label: "Tipo de negocio" },
      { label: "Estado" },
    ]
    : [
      { label: "Alias" },
      { label: "Correo electrónico" },
      { label: "Tipo de negocio" },
      { label: "API Key" },
      { label: "Estado" },
      { label: "Acciones" },
    ];

  return (
    <TableContainer component={Paper} className="m-t-40 br-20 bg-light">
      <Table>
        <ListHeader columns={tableColumns} />
        <TableBody>
          {clients.map((client) => {
            const { id, name, email, disabled, entity, businessName, apiKey } = client;
            const displayName = businessName || name || "-";
            const entityType = entity?.type || "-";

            return (
              <TableRow key={id}>
                {showStatusColumn ? (
                  // Invitations view: email first, no apiKey column
                  <>
                    <TableCell className="p-l-20">{email}</TableCell>
                    <TableCell>{displayName}</TableCell>
                    <TableCell className="capitalize">{entityType}</TableCell>
                    <TableCell>Pendiente</TableCell>
                  </>
                ) : (
                  // Integrations view: name first, with apiKey and actions
                  <>
                    <TableCell className="p-l-20">{displayName}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell className="capitalize">{entityType}</TableCell>
                    <ApiKeyCell apiKey={apiKey} />
                    {handleDisableAction ? (
                      <DisableAction
                        id={id}
                        name={displayName}
                        disabled={disabled}
                        handleDisableAction={handleDisableAction}
                        resetValue={resetValue}
                      />
                    ) : (
                      <TableCell>
                        {disabled ? "Deshabilitado" : "Habilitado"}
                      </TableCell>
                    )}
                    {handleSelectedAction && setSelectedClient && (
                      <TableActions
                        client={client}
                        setSelectedClient={setSelectedClient}
                        handleSelectedAction={handleSelectedAction}
                      />
                    )}
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ListPagination
        total={count}
        page={page}
        onChangePage={handlePageChange}
      />
    </TableContainer>
  );
};

export default ListInternalClients;
